import React, { useState, useEffect } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Inject, Sort, Filter } from '@syncfusion/ej2-react-grids';
import { RiCloseCircleLine } from "react-icons/ri";
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { useStateContext } from '../contexts/ContextProvider';
import axios from 'axios';

const NavButton = ({ title, customFunc, icon, color, size = 'xl', padding = 'p-3' }) => (
  <TooltipComponent content={title} position="BottomCenter">
    <button
      type="button"
      onClick={customFunc}
      style={{ color }}
      className={`relative text-${size} rounded-full ${padding} hover:bg-light-gray`}
    >
      {icon}
    </button>
  </TooltipComponent>
);

const Toast = ({ toast }) => {
  if (!toast) return null;
  const isError = toast.type === 'error';
  return (
    <div
      className="rounded-lg px-4 py-3 mb-3 text-sm font-medium"
      style={{
        backgroundColor: isError ? '#fee2e2' : '#fefce8',
        border: `1px solid ${isError ? '#f87171' : '#facc15'}`,
        color: isError ? '#991b1b' : '#854d0e',
      }}
    >
      {toast.message}
    </div>
  );
};

const DetalleStock = ({ onClose, stock = {}, categoria, tabIndex }) => {
  const { currentColor } = useStateContext();
  const [stockData, setStockData] = useState(stock);
  const [recargasHistorico, setRecargasHistorico] = useState([]);
  const [activeButton, setActiveButton] = useState(null);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const esBodega = tabIndex === 0;
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 6000);
  };

  useEffect(() => {
    fetchHistorico();
  }, [stock.id]);

  const fetchHistorico = async () => {
    if (!stock.id || categoria !== 'lamina') return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/historico-stock/`, {
        params: { lamina: stock.id },
        headers: authHeader(),
      });
      setRecargasHistorico(response.data);
    } catch (error) {
      console.error('Error fetching historial:', error);
    }
  };

  const refreshStock = async () => {
    if (!stock.id || categoria !== 'lamina') return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/productos/laminas/${stock.id}/`,
        { headers: authHeader() }
      );
      setStockData(response.data);
    } catch (error) {
      console.error('Error refrescando stock:', error);
    }
  };

  const handleButtonClick = (button) => {
    setActiveButton(button);
    if (button === 'editDatos') {
      setFormData({
        nombre: stockData.nombre || '',
        descripcion: stockData.descripcion || '',
        marca: stockData.marca || '',
        rendimiento: stockData.rendimiento || '',
        preciom2: stockData.preciom2 || '',
        costo_m2: stockData.costo_m2 || '',
      });
    } else {
      setFormData({ cantidad: '', comentario: '' });
    }
  };

  const handleCancel = () => {
    setActiveButton(null);
    setFormData({});
  };

  const isSaveDisabled = () => {
    if (activeButton === 'editDatos') {
      return !formData.nombre?.trim();
    }
    const motivoValido = formData.motivoPredef && formData.motivoPredef !== 'Otro'
      ? true
      : formData.comentario?.trim();
    return !formData.cantidad || parseFloat(formData.cantidad) <= 0 || !motivoValido;
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      let response;
      const id = stock.id;

      if (activeButton === 'editDatos') {
        response = await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/api/productos/laminas/${id}/`,
          formData,
          { headers: authHeader() }
        );
        setStockData(response.data);
        setHasChanges(true);
        showToast('info', 'Datos actualizados correctamente.');

      } else if (activeButton === 'agregarStock') {
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/productos/laminas/${id}/recargar/`,
          { cantidad: formData.cantidad, comentario: formData.comentario },
          { headers: authHeader() }
        );
        await Promise.all([refreshStock(), fetchHistorico()]);
        setHasChanges(true);
        showToast('info', `Stock agregado correctamente.`);

      } else if (activeButton === 'consumirStock') {
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/productos/laminas/${id}/consumir/`,
          { cantidad: formData.cantidad, comentario: formData.comentario },
          { headers: authHeader() }
        );
        await Promise.all([refreshStock(), fetchHistorico()]);
        setHasChanges(true);
        showToast('info', 'Stock consumido correctamente.');

      } else if (activeButton === 'transferir') {
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/productos/laminas/${id}/transferir/`,
          { cantidad: formData.cantidad, comentario: formData.comentario },
          { headers: authHeader() }
        );
        await Promise.all([refreshStock(), fetchHistorico()]);
        setHasChanges(true);
        showToast('info', `Transferencia realizada. Bodega: ${response.data.bodega?.stock_m2} m² — Auto: ${response.data.auto?.stock_m2} m²`);
      }

      setActiveButton(null);
      setFormData({});
    } catch (error) {
      const msg = error.response?.data?.detail || 'Ocurrió un error al guardar.';
      showToast('error', msg);
    } finally {
      setIsSaving(false);
    }
  };

  const renderForm = () => {
    if (!activeButton) return null;

    if (activeButton === 'editDatos') {
      return (
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-sm" style={{ color: '#FF743A' }}>Editar Datos</p>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Nombre *</label>
            <input type="text" value={formData.nombre || ''} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} className="border p-2 rounded-lg text-sm" />
          </div>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <label className="text-xs text-gray-500 mb-1">Marca</label>
              <input type="text" value={formData.marca || ''} onChange={(e) => setFormData({ ...formData, marca: e.target.value })} className="border p-2 rounded-lg text-sm" />
            </div>
            <div className="flex flex-col flex-1">
              <label className="text-xs text-gray-500 mb-1">Rendimiento (m²/rollo)</label>
              <input type="number" value={formData.rendimiento || ''} onChange={(e) => setFormData({ ...formData, rendimiento: e.target.value })} className="border p-2 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <label className="text-xs text-gray-500 mb-1">Precio m²</label>
              <input type="number" value={formData.preciom2 || ''} onChange={(e) => setFormData({ ...formData, preciom2: e.target.value })} className="border p-2 rounded-lg text-sm" />
            </div>
            <div className="flex flex-col flex-1">
              <label className="text-xs text-gray-500 mb-1">Costo m²</label>
              <input type="number" value={formData.costo_m2 || ''} onChange={(e) => setFormData({ ...formData, costo_m2: e.target.value })} className="border p-2 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Descripción</label>
            <input type="text" value={formData.descripcion || ''} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} className="border p-2 rounded-lg text-sm" />
          </div>
        </div>
      );
    }

    const labels = {
      agregarStock:  { title: 'Agregar Stock',       cantLabel: 'm² a agregar',    placeholder: 'Ej: 20.00' },
      consumirStock: { title: 'Consumir Stock',       cantLabel: 'm² a consumir',   placeholder: 'Ej: 5.00' },
      transferir:    { title: 'Transferir a Auto',    cantLabel: 'm² a transferir', placeholder: 'Ej: 10.00' },
    };
    const { title, cantLabel, placeholder } = labels[activeButton];

    const motivosPorAccion = {
      consumirStock: [
        'Merma',
        'Muestra a cliente',
        'Defecto de material',
        'Daño en transporte',
        'Pérdida',
        'Ajuste de inventario',
        'Otro',
      ],
      agregarStock: [
        'Compra a proveedor',
        'Devolución de instalación',
        'Reingreso por garantía',
        'Ajuste de inventario',
        'Otro',
      ],
      transferir: [
        'Carga semanal',
        'Preparación para instalación',
        'Reposición preventiva',
        'Ajuste de inventario',
        'Otro',
      ],
    };

    const motivosActivos = motivosPorAccion[activeButton] || null;
    const isComentarioCustom = motivosActivos && formData.motivoPredef === 'Otro';

    return (
      <div className="flex flex-col gap-2">
        <p className="font-semibold text-sm" style={{ color: '#FF743A' }}>{title}</p>
        <div className="flex gap-2">
          <div className="flex flex-col flex-1">
            <label className="text-xs text-gray-500 mb-1">{cantLabel}</label>
            <input
              type="number"
              value={formData.cantidad || ''}
              onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })}
              className="border p-2 rounded-lg text-sm"
              placeholder={placeholder}
              min="1"
              step="1"
            />
          </div>
          <div className="flex flex-col flex-1">
            <label className="text-xs text-gray-500 mb-1">Motivo</label>
            {motivosActivos ? (
              <>
                <select
                  value={formData.motivoPredef || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    motivoPredef: e.target.value,
                    comentario: e.target.value !== 'Otro' ? e.target.value : '',
                  })}
                  className="border p-2 rounded-lg text-sm"
                >
                  <option value="">Selecciona un motivo</option>
                  {motivosActivos.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {isComentarioCustom && (
                  <input
                    type="text"
                    value={formData.comentario || ''}
                    onChange={(e) => setFormData({ ...formData, comentario: e.target.value })}
                    className="border p-2 rounded-lg text-sm mt-1"
                    placeholder="Describe el motivo"
                    autoFocus
                  />
                )}
              </>
            ) : (
              <input
                type="text"
                value={formData.comentario || ''}
                onChange={(e) => setFormData({ ...formData, comentario: e.target.value })}
                className="border p-2 rounded-lg text-sm"
                placeholder="Motivo"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStockDetails = (s = stockData) => {
    if (!s || Object.keys(s).length === 0) return <p>No hay información del stock disponible.</p>;
    switch (categoria) {
      case 'lamina':
        return (
          <>
            <div className="left-info">
              <p><strong>Código:</strong> {s.codigo || 'N/A'}</p>
              <p><strong>Nombre:</strong> {s.nombre || 'N/A'}</p>
              <p><strong>Marca:</strong> {s.marca || 'N/A'}</p>
              <p><strong>Descripción:</strong> {s.descripcion || ''}</p>
            </div>
            <div className="right-info">
              <p><strong>Precio m²:</strong> {parseFloat(s.preciom2 || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</p>
              <p><strong>Costo m²:</strong> {parseFloat(s.costo_m2 || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</p>
              <p><strong>Rendimiento por rollo:</strong> {s.rendimiento || 'N/A'} m²</p>
              <p><strong>Stock disponible:</strong> {s.stock_m2 || '0'} m²</p>
              <p>
                <strong>Estado: </strong>
                <span style={{ color: s.tiene_stock_bajo ? 'red' : 'green' }}>
                  {s.tiene_stock_bajo ? 'Stock bajo' : 'Normal'}
                </span>
              </p>
            </div>
          </>
        );
      case 'herramienta':
        return (
          <>
            <div className="left-info">
              <p><strong>Código:</strong> {s.codigo || 'N/A'}</p>
              <p><strong>Nombre:</strong> {s.nombre || 'N/A'}</p>
              <p><strong>Marca:</strong> {s.marca || 'N/A'}</p>
            </div>
            <div className="right-info">
              <p><strong>Unidades en stock:</strong> {s.unidades || '0'}</p>
              <p><strong>Descripción:</strong> {s.descripcion || 'N/A'}</p>
            </div>
          </>
        );
      case 'vehiculo':
        return (
          <>
            <div className="left-info">
              <p><strong>Patente:</strong> {s.codigo || 'N/A'}</p>
              <p><strong>Modelo:</strong> {s.modelo || 'N/A'}</p>
              <p><strong>Nombre:</strong> {s.nombre || 'N/A'}</p>
            </div>
            <div className="right-info">
              <p><strong>Marca:</strong> {s.marca || 'N/A'}</p>
              <p><strong>Descripción:</strong> {s.descripcion}</p>
            </div>
          </>
        );
      default:
        return <p>No hay detalles disponibles para esta categoría.</p>;
    }
  };

  return (
    <div className="bg-half-transparent w-screen fixed nav-item top-0 right-0 h-full flex">
      <div className="responsive-div p-6" style={{ backgroundColor: 'white', overflowY: 'hidden' }}>

        {/* Zona scrolleable */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: '8px' }}>
          <div className="flex justify-between items-center mb-6">
            <p className="font-semibold text-lg" style={{ color: '#FF743A' }}>Detalle de Stock</p>
            <NavButton title="Cerrar" customFunc={() => onClose(hasChanges)} color={currentColor} icon={<RiCloseCircleLine />} size="3xl" padding="p-4" />
          </div>

          <div className="cotizacion-header">
            <h1 className="cotizacion-title">DETALLE STOCK</h1>
          </div>

          <div className="cliente-info">
            {renderStockDetails()}
          </div>

          {categoria === 'lamina' && (
            <div className="mt-6">
              <h2 className="font-semibold text-lg" style={{ color: '#FF743A' }}>Histórico de Movimientos</h2>
              <div style={{ overflowX: 'auto', minWidth: 0 }}>
                <GridComponent
                  dataSource={recargasHistorico}
                  allowPaging={true}
                  pageSettings={{ pageCount: 5 }}
                  allowSorting={true}
                  allowFiltering={true}
                  allowTextWrap={true}
                  textWrapSettings={{ wrapMode: 'Content' }}
                >
                  <ColumnsDirective>
                    <ColumnDirective field="fecha" headerText="Fecha" width="110" textAlign="Center" />
                    <ColumnDirective field="tipo_modificacion" headerText="Tipo" width="130" textAlign="Center" />
                    <ColumnDirective field="cantidad" headerText="Cantidad (m²)" width="120" textAlign="Center" />
                    <ColumnDirective field="comentario" headerText="Comentario" minWidth="150" textAlign="Left" clipMode="EllipsisWithTooltip" />
                  </ColumnsDirective>
                  <Inject services={[Page, Sort, Filter]} />
                </GridComponent>
              </div>
            </div>
          )}
        </div>

        {/* Formulario + botones fijos */}
        <div style={{ flexShrink: 0, borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
          <Toast toast={toast} />

          {activeButton && (
            <div className="mb-3 p-3 rounded-lg" style={{ backgroundColor: '#fff7f3', border: '1px solid #FF743A' }}>
              {renderForm()}
            </div>
          )}

          <div className="stock-actions flex justify-between gap-2">
            {activeButton === null ? (
              <>
                {tabIndex !== 1 && (
                  <button
                    type="button"
                    className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition duration-200"
                    onClick={() => handleButtonClick('editDatos')}
                  >
                    Editar Datos
                  </button>
                )}
                <button
                  type="button"
                  className="bg-orange-500 text-white p-2 rounded-lg hover:bg-orange-600 transition duration-200"
                  onClick={() => handleButtonClick('consumirStock')}
                >
                  Consumir
                </button>
                {esBodega && (
                  <>
                    <button
                      type="button"
                      className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition duration-200"
                      onClick={() => handleButtonClick('agregarStock')}
                    >
                      Agregar Stock
                    </button>
                    <button
                      type="button"
                      className="bg-purple-500 text-white p-2 rounded-lg hover:bg-purple-600 transition duration-200"
                      onClick={() => handleButtonClick('transferir')}
                    >
                      Transferir
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition duration-200"
                  onClick={handleCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="text-white p-2 rounded-lg transition duration-200 flex items-center gap-2"
                  style={{
                    backgroundColor: (isSaveDisabled() || isSaving) ? '#9ca3af' : '#22c55e',
                    cursor: (isSaveDisabled() || isSaving) ? 'not-allowed' : 'pointer'
                  }}
                  onClick={handleSave}
                  disabled={isSaveDisabled() || isSaving}
                >
                  {isSaving && <span className="loader" />}
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalleStock;
